import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ShoppingCart, 
  Loader2,
  CreditCard,
  ShieldCheck,
  Package,
  Upload,
  FileCheck,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateFileId, setCertificateFileId] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);
  
  const [customer, setCustomer] = useState({
    full_name: "",
    company_name: "",
    email: "",
    phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip: "",
    billing_address: "",
    billing_city: "",
    billing_state: "",
    billing_zip: "",
    notes: "",
    tax_exempt_id: ""
  });

  useEffect(() => {
    const savedCart = localStorage.getItem('clearpath_cart');
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      if (Object.keys(parsedCart).length === 0) {
        navigate('/shop');
        return;
      }
      setCart(parsedCart);
    } else {
      navigate('/shop');
      return;
    }
    fetchProducts();
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shop/products`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomer(prev => ({ ...prev, [name]: value }));
    
    // Sync billing with shipping if checkbox is checked
    if (sameAsShipping && name.startsWith('shipping_')) {
      const billingField = name.replace('shipping_', 'billing_');
      setCustomer(prev => ({ ...prev, [billingField]: value }));
    }
  };

  const handleSameAsShippingChange = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      setCustomer(prev => ({
        ...prev,
        billing_address: prev.shipping_address,
        billing_city: prev.shipping_city,
        billing_state: prev.shipping_state,
        billing_zip: prev.shipping_zip
      }));
    }
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a PDF, JPEG, PNG, or WebP file");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      e.target.value = "";
      return;
    }
    
    setUploadingCert(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/shop/upload-certificate`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setCertificateFile(file.name);
      setCertificateFileId(data.file_id);
      toast.success("Certificate uploaded successfully");
    } catch {
      toast.error("Failed to upload certificate");
      e.target.value = "";
    } finally {
      setUploadingCert(false);
    }
  };

  const removeCertificate = () => {
    setCertificateFile(null);
    setCertificateFileId(null);
  };

  const cartItems = products.filter(p => cart[p.id]).map(p => ({
    ...p,
    quantity: cart[p.id],
    subtotal: p.price * cart[p.id]
  }));

  const cartTotal = cartItems.reduce((sum, item) => sum + item.subtotal, 0);

  const validateForm = () => {
    const required = ['full_name', 'company_name', 'email', 'phone', 
                      'shipping_address', 'shipping_city', 'shipping_state', 'shipping_zip'];
    
    for (const field of required) {
      if (!customer[field]?.trim()) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    
    if (!customer.email.includes('@')) {
      toast.error("Please enter a valid email address");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      const items = Object.entries(cart).map(([product_id, quantity]) => ({
        product_id,
        quantity
      }));
      
      const billingInfo = sameAsShipping ? {
        billing_address: customer.shipping_address,
        billing_city: customer.shipping_city,
        billing_state: customer.shipping_state,
        billing_zip: customer.shipping_zip
      } : {
        billing_address: customer.billing_address,
        billing_city: customer.billing_city,
        billing_state: customer.billing_state,
        billing_zip: customer.billing_zip
      };
      
      const response = await fetch(`${BACKEND_URL}/api/shop/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          customer: { ...customer, ...billingInfo, tax_certificate_file_id: certificateFileId || null },
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to process checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
          <button 
            onClick={() => navigate('/shop')} 
            className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-4"
            data-testid="back-to-shop"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Shop
          </button>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="checkout-headline">
            Checkout
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Customer Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={customer.full_name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_name">Company / Clinic Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={customer.company_name}
                      onChange={handleChange}
                      placeholder="ABC Medical Clinic"
                      required
                      data-testid="checkout-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={customer.email}
                      onChange={handleChange}
                      placeholder="john@abcmedical.com"
                      required
                      data-testid="checkout-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={customer.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                      data-testid="checkout-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Shipping Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="shipping_address">Street Address *</Label>
                    <Input
                      id="shipping_address"
                      name="shipping_address"
                      value={customer.shipping_address}
                      onChange={handleChange}
                      placeholder="123 Medical Center Dr"
                      required
                      data-testid="checkout-shipping-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="shipping_city">City *</Label>
                      <Input
                        id="shipping_city"
                        name="shipping_city"
                        value={customer.shipping_city}
                        onChange={handleChange}
                        placeholder="Dallas"
                        required
                        data-testid="checkout-shipping-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping_state">State *</Label>
                      <Input
                        id="shipping_state"
                        name="shipping_state"
                        value={customer.shipping_state}
                        onChange={handleChange}
                        placeholder="TX"
                        required
                        data-testid="checkout-shipping-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping_zip">ZIP Code *</Label>
                      <Input
                        id="shipping_zip"
                        name="shipping_zip"
                        value={customer.shipping_zip}
                        onChange={handleChange}
                        placeholder="75201"
                        required
                        data-testid="checkout-shipping-zip"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Billing Address
                  </h2>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="same_as_shipping"
                      checked={sameAsShipping}
                      onCheckedChange={handleSameAsShippingChange}
                      data-testid="same-as-shipping"
                    />
                    <Label htmlFor="same_as_shipping" className="text-sm cursor-pointer">
                      Same as shipping
                    </Label>
                  </div>
                </div>
                
                {!sameAsShipping && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="billing_address">Street Address *</Label>
                      <Input
                        id="billing_address"
                        name="billing_address"
                        value={customer.billing_address}
                        onChange={handleChange}
                        placeholder="123 Billing St"
                        required
                        data-testid="checkout-billing-address"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="billing_city">City *</Label>
                        <Input
                          id="billing_city"
                          name="billing_city"
                          value={customer.billing_city}
                          onChange={handleChange}
                          placeholder="Dallas"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing_state">State *</Label>
                        <Input
                          id="billing_state"
                          name="billing_state"
                          value={customer.billing_state}
                          onChange={handleChange}
                          placeholder="TX"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing_zip">ZIP Code *</Label>
                        <Input
                          id="billing_zip"
                          name="billing_zip"
                          value={customer.billing_zip}
                          onChange={handleChange}
                          placeholder="75201"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Information */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Additional Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="notes">Order Notes / Special Instructions</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={customer.notes}
                      onChange={handleChange}
                      placeholder="Delivery instructions, PO number, etc."
                      rows={3}
                      data-testid="checkout-notes"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tax_exempt_id">Tax Exempt / Resale Certificate ID (Optional)</Label>
                    <Input
                      id="tax_exempt_id"
                      name="tax_exempt_id"
                      value={customer.tax_exempt_id}
                      onChange={handleChange}
                      placeholder="Enter certificate number if applicable"
                      data-testid="checkout-tax-exempt"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      If tax exempt, our team will verify and adjust your order accordingly.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="tax_certificate_upload">Tax-Exempt / Resale Certificate Upload (Optional)</Label>
                    {!certificateFile ? (
                      <div className="mt-1">
                        <label
                          htmlFor="tax_certificate_upload"
                          className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-sky-400 hover:bg-sky-50/50 transition-colors"
                          data-testid="certificate-upload-label"
                        >
                          {uploadingCert ? (
                            <Loader2 className="w-5 h-5 text-sky-600 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-600">
                            {uploadingCert ? "Uploading..." : "Click to upload PDF, JPEG, or PNG (max 10MB)"}
                          </span>
                        </label>
                        <input
                          id="tax_certificate_upload"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          className="hidden"
                          onChange={handleCertificateUpload}
                          disabled={uploadingCert}
                          data-testid="certificate-upload-input"
                        />
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="certificate-uploaded-indicator">
                        <FileCheck className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm text-emerald-800 flex-1 truncate">{certificateFile}</span>
                        <button
                          type="button"
                          onClick={removeCertificate}
                          className="p-1 hover:bg-emerald-100 rounded"
                          data-testid="certificate-remove-btn"
                        >
                          <X className="w-4 h-4 text-emerald-600" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      Upload your tax-exempt or resale certificate for faster processing.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 border border-slate-200 sticky top-24">
                <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Order Summary
                </h2>
                
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex justify-between items-start pb-4 border-b border-slate-100">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">{item.name}</p>
                        <p className="text-xs text-slate-500">
                          {item.quantity} × ${item.price.toFixed(2)} per {item.packaging_unit || "unit"}
                        </p>
                        {item.item_code && (
                          <p className="text-xs text-slate-400">{item.item_code} &middot; {item.packaging_display}</p>
                        )}
                      </div>
                      <p className="font-medium text-slate-900">${item.subtotal.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Shipping</span>
                    <span className="text-sm">Calculated at fulfillment</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Tax</span>
                    <span className="text-sm">Calculated at fulfillment</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-200">
                    <span>Total Due</span>
                    <span className="text-sky-600">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-base"
                  data-testid="checkout-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Proceed to Payment
                    </>
                  )}
                </Button>
                
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Secure checkout powered by Stripe</span>
                </div>
                
                <p className="text-xs text-slate-500 mt-4 text-center">
                  By placing this order, you agree to our terms of service and confirm this is for professional healthcare use only.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
